import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { ContentNugget, CareerPath } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { NuggetCardItem } from '@backrs_ui/grow-ui/src/components/NuggetCards/NuggetCardItem';
import { handlePanelNuggetClick } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { CareerPathSearchParentCategory } from '@backrs_ui/grow-ui/src/models/models';
import { SearchFiltersGroup } from '@backrs_ui/grow-ui/src/pages/Search/components/SearchFiltersGroup';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { useAgentContext } from './ResourceNuggetSection.agentContext';
import { CAREER_PATH_CONSTANTS } from '../constants';
import { useResourceNuggetSectionSearch } from '../hooks/useResourceNuggetSectionSearch';

const STRINGS = appStrings.modules.careerPath.detailPage.resources;

type ResourceNuggetSectionProps = Readonly<{
  careerPath: CareerPath;
  category: CareerPathSearchParentCategory;
  sectionTitle: string;
}>;

export function ResourceNuggetSection({
  careerPath,
  category,
  sectionTitle,
}: ResourceNuggetSectionProps) {
  const navigate = useNavigate();

  const { searchResults, loadMoreResults, combinedResults, hasMore, isLoadingMore } =
    useResourceNuggetSectionSearch(careerPath.name, category);

  useAgentContext(sectionTitle, combinedResults);

  const handlePanelNuggetClickWrapper = useCallback(
    (nugget: ContentNugget) => {
      handlePanelNuggetClick(nugget, navigate);
    },
    [navigate],
  );

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.target as HTMLDivElement;
      const { scrollLeft, scrollWidth, clientWidth } = target;
      const scrollPercentage = (scrollLeft + clientWidth) / scrollWidth;

      if (
        scrollPercentage > CAREER_PATH_CONSTANTS.LAZY_LOAD.SCROLL_THRESHOLD &&
        hasMore &&
        !isLoadingMore
      ) {
        loadMoreResults();
      }
    },
    [hasMore, isLoadingMore, loadMoreResults],
  );

  // Get the search state to check initial result count
  const searchState = useAppSelector((state) => state.careerPathSearch);
  const sectionSearchParams = searchState.sections[category.name];
  const initialResultCount = sectionSearchParams?.initialResultCount;

  // Only hide the section if there are no results AND the initial search had no results
  if (searchResults && searchResults.total === 0 && initialResultCount === 0) {
    return null;
  }

  return (
    <Box>
      {/* Display search results */}
      <Box sx={contentContainerStyle}>
        {combinedResults.length > 0 && (
          <Box sx={horizontalNuggetContainerStyle} onScroll={handleScroll}>
            {combinedResults.map((nugget: ContentNugget) => (
              <Box
                id={`career-path-nugget-${nugget.variant}-${nugget.id}`}
                key={`${nugget.title}-${nugget.variant}-${nugget.id}`}
                sx={nuggetCardStyle}
              >
                <NuggetCardItem
                  key={`${nugget.title}-${nugget.variant}-${nugget.id}-nugget`}
                  nugget={nugget}
                  onClick={handlePanelNuggetClickWrapper}
                />
              </Box>
            ))}
            {isLoadingMore && (
              <Box sx={loadingIndicatorStyle}>
                <Typography variant="text-sm-regular">{STRINGS.loadingMore}</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export function ResourceNuggetSectionFilter({
  careerPathName,
  category,
}: {
  careerPathName: string;
  category: CareerPathSearchParentCategory;
}) {
  const { selectedFilters, handleSettingSelectedItems } = useResourceNuggetSectionSearch(
    careerPathName,
    category,
  );

  const modifiedCategory = {
    ...category,
    name: STRINGS.filter,
    iconName: 'FilterLines' as const,
  };

  return (
    <SearchFiltersGroup
      selectedItems={selectedFilters}
      setSelectedItems={handleSettingSelectedItems}
      categories={[modifiedCategory]}
    />
  );
}

const nuggetCardStyle: SxProps<Theme> = ({ backrs }) => ({
  flexShrink: 0,
  width: '286px',
  '&:last-child': {
    marginRight: backrs.spacing['spacing-2xl'],
  },
  '@container (max-width: 600px)': {
    scrollSnapAlign: 'center',
  },
});

const loadingIndicatorStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '274px',
  height: '100px',
  flexShrink: 0,
  color: backrs.colors['Colors/Text/text-secondary'],
});

const contentContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  mt: backrs.spacing['spacing-2xl'],
});

const horizontalNuggetContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-xl'],
  overflowX: 'auto',
  overflowY: 'hidden',
  paddingTop: backrs.spacing['spacing-xs'],
  paddingLeft: backrs.spacing['spacing-xs'],
  paddingBottom: backrs.spacing['spacing-lg'],
  scrollbarWidth: 'thin',
  scrollbarColor: `${backrs.colors['Colors/Border/border-secondary']} transparent`,
  containerType: 'inline-size',
  '@container (max-width: 600px)': {
    scrollSnapType: 'x mandatory',
  },
  '&::-webkit-scrollbar': {
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: backrs.colors['Colors/Border/border-secondary'],
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: backrs.colors['Colors/Border/border-primary'],
  },
});
